import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ABOUT_IMAGE } from "../lib/media";

const GEAR = [
    { cat: "Camera Body", items: "Sony A7 IV · Sony A7R IV" },
    { cat: "Lenti", items: "Sony 70-200 f/2.8 GM · Sigma 24-70 f/2.8 II · Sigma 85 f/1.4 · Sony 200-600" },
    { cat: "Drone", items: "DJI Mavic 3 Pro · DJI Mini 4 Pro" },
    { cat: "Audio", items: "Rode Wireless Pro · Sennheiser MKE 600 · Zoom H5" },
    { cat: "Illuminazione", items: "Aputure 300D II · Godox AD200 · Softbox octa 120" },
    { cat: "Post", items: "Lightroom · Photoshop · DaVinci Resolve · Premiere Pro" },
];

export default function About() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section
            id="about"
            ref={ref}
            className="relative bg-ink-950 py-20 md:py-40 px-5 md:px-10 border-t border-white/10"
            data-testid="about-section"
        >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
                {/* Left image with parallax — Francesco portrait */}
                <div className="md:col-span-5 relative aspect-[3/4] md:aspect-auto md:h-[90vh] overflow-hidden ring-1 ring-white/10 group">
                    <motion.img
                        style={{ y }}
                        src={ABOUT_IMAGE}
                        alt="Francesco Ciotoli — fotografo e videomaker Ceccano Frosinone"
                        className="absolute inset-0 w-full h-[115%] object-cover object-[center_20%] transition-all duration-1000 group-hover:scale-105"
                        data-testid="about-image"
                    />
                    {/* Elegant vignette overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-ink-950/10 to-transparent pointer-events-none" />
                    <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />

                    {/* Name plate */}
                    <div className="absolute bottom-6 left-4 right-4 md:left-6 md:right-6 flex items-end justify-between">
                        <div>
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-flash mb-2">
                                Il fotografo
                            </div>
                            <div className="font-display uppercase text-bone-50 leading-[0.9] tracking-[-0.02em]" style={{ fontSize: "clamp(1.4rem, 2.4vw, 2.4rem)" }}>
                                Francesco<br />Ciotoli<span className="text-flash">.</span>
                            </div>
                        </div>
                        <span className="hidden sm:inline font-mono text-[10px] tracking-[0.2em] uppercase text-bone-50/70">
                            Sony · 85mm
                        </span>
                    </div>
                </div>

                {/* Right editorial text */}
                <div className="md:col-span-7 md:pl-10">
                    <div className="overline mb-6">
                        <span className="text-flash">■</span>&nbsp;&nbsp;03 — Chi sono
                    </div>
                    <h2
                        className="font-display uppercase text-bone-50 leading-[0.85] tracking-[-0.03em]"
                        style={{ fontSize: "clamp(2.4rem, 6vw, 6rem)" }}
                        data-testid="about-heading"
                    >
                        Occhio,<br />
                        <span className="font-serif-i normal-case tracking-normal text-bone-400">istinto</span>,<br />
                        movimento<span className="text-flash">.</span>
                    </h2>

                    <div className="mt-8 max-w-2xl text-bone-200 font-light text-base md:text-lg leading-relaxed" data-testid="about-copy">
                        <p>
                            <span className="float-left font-display text-flash mr-3" style={{ fontSize: "3.5rem", lineHeight: 0.75 }}>F</span>
                            rancesco Ciotoli è un fotografo e videomaker con base a Ceccano (Frosinone).
                            Racconta lo sport, le persone e i luoghi con un linguaggio
                            <span className="font-serif-i text-bone-50"> cinematografico</span> e diretto.
                            Lavora con squadre, atleti, brand e magazine — dallo scatto in campo alla
                            copertina editoriale, dal video-clip alla ripresa aerea con drone.
                        </p>
                        <p className="mt-5 text-bone-400">
                            La sua firma sta nel modo in cui unisce fotografia, video e grafica in un unico
                            racconto: la luce guida, il montaggio scandisce, il colore fissa il tono.
                        </p>
                    </div>

                    {/* Gear list */}
                    <div className="mt-14">
                        <div className="overline mb-6">Strumentazione</div>
                        <dl className="divide-y divide-white/10 border-y border-white/10">
                            {GEAR.map((g) => (
                                <motion.div
                                    key={g.cat}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-15%" }}
                                    transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                                    className="py-4 grid grid-cols-12 gap-4 items-baseline"
                                    data-testid={`gear-${g.cat}`}
                                >
                                    <dt className="col-span-12 md:col-span-4 font-mono text-[11px] tracking-[0.2em] uppercase text-bone-400">
                                        {g.cat}
                                    </dt>
                                    <dd className="col-span-12 md:col-span-8 text-bone-50 font-light">{g.items}</dd>
                                </motion.div>
                            ))}
                        </dl>
                    </div>

                    {/* Stats */}
                    <div className="mt-14 grid grid-cols-3 gap-6">
                        {[
                            { k: "5+", v: "Anni di esperienza" },
                            { k: "200+", v: "Progetti consegnati" },
                            { k: "40+", v: "Clienti soddisfatti" },
                        ].map((s) => (
                            <div key={s.v} className="border-t border-white/10 pt-4" data-testid={`stat-${s.k}`}>
                                <div className="font-display uppercase text-bone-50 leading-none" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                                    {s.k}<span className="text-flash">.</span>
                                </div>
                                <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-bone-400">{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
